import {
  BuildingOffice2Icon,
  CircleStackIcon,
  HomeModernIcon,
  KeyIcon,
  ChartBarIcon,
  CheckBadgeIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { KPICard } from "./KPICard";
import { useDashboardKPIs, useDashboardInventory } from "@/api/dashboard";
import { BrandedLoader } from "@/components/shared/BrandedLoader";

export function KPIGrid() {
  const { data: kpis, isLoading: kpiLoading } = useDashboardKPIs();
  const { data: inv, isLoading: invLoading } = useDashboardInventory();

  if (kpiLoading || invLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <BrandedLoader size={40} label={null} />
      </div>
    );
  }

  const p = inv?.properties;
  const r = inv?.rentals;

  return (
    <div className="space-y-2">
      {/* Row 0: Available (on-market) inventory — active listing, residential
          only. Leads with the total; split into Secondary / Ready-to-move
          (developer, primary completed) / Off-Plan. */}
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        <KPICard
          label="Available Properties"
          value={(p?.available_total ?? 0).toLocaleString()}
          icon={<HomeModernIcon className="h-6 w-6" />}
          color="blue"
          href="/properties?listing_status=active"
          subLabel="On the market now — active listing, residential only (excludes land, commercial and sold/expired)."
        />
        <KPICard
          label="Secondary"
          value={(p?.available_secondary ?? 0).toLocaleString()}
          icon={<HomeModernIcon className="h-6 w-6" />}
          color="teal"
          href="/properties?listing_status=active"
          subLabel="Ready-to-move resale (completed / ready)."
        />
        <KPICard
          label="Ready to Move (developer)"
          value={(p?.available_completed_primary ?? 0).toLocaleString()}
          icon={<CheckBadgeIcon className="h-6 w-6" />}
          color="emerald"
          href="/properties?listing_status=active"
          subLabel="Completed primary — developer first-sale, ready to move."
        />
        <KPICard
          label="Off-Plan"
          value={(p?.available_off_plan ?? 0).toLocaleString()}
          icon={<ChartBarIcon className="h-6 w-6" />}
          color="amber"
          href="/properties?listing_status=active"
          subLabel="Off-plan + under-construction."
        />
      </div>

      {/* Row 2 (MPPI-00157): four cards, matching the four above, so the two
          rows line up. Deal Packs and Active Deals were dropped — neither was
          being used — and Data Age moved up from the row below, which no
          longer exists. */}
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        <KPICard
          label="Rental Properties"
          value={(r?.total ?? 0).toLocaleString()}
          icon={<KeyIcon className="h-6 w-6" />}
          color="teal"
          href="/rental-properties"
          subLabel={
            r
              ? `Deduplicated for-rent units. ${r.below_market.toLocaleString()} asking below market${r.avg_implied_yield != null ? ` · avg implied yield ${r.avg_implied_yield}%` : ""}.`
              : "Deduplicated for-rent units — market-rent AVM, rent-vs-market, implied yield."
          }
        />
        {/* F-022 — Expired Properties: no longer on the market
            (withdrawn / sold / expired). Click-through opens the
            Properties list pre-filtered to the cohort. Companion to
            the "Currently Listed" toggle on the Properties page. */}
        <KPICard
          label="Expired Properties"
          value={(p?.expired_properties ?? 0).toLocaleString()}
          icon={<ClockIcon className="h-6 w-6" />}
          color="rose"
          href="/properties?listing_status=expired"
          subLabel="No longer on the market — withdrawn, sold or expired. Retained for AVM comparables and historic pricing."
        />
        {/* Off-plan pipeline. Classified exactly as the By Developer chart
            classifies it — in-progress is a non-ready project handing over
            within two years, planned is beyond that or with no handover year
            at all — so the dashboard and that chart cannot disagree about
            what "off-plan" counts. */}
        <KPICard
          label="Off-Plan Projects"
          value={(p?.offplan_projects?.total ?? 0).toLocaleString()}
          icon={<BuildingOffice2Icon className="h-6 w-6" />}
          color="violet"
          href="/market/offplan"
          subLabel={
            p?.offplan_projects
              ? `${p.offplan_projects.in_progress.toLocaleString()} under construction · ${p.offplan_projects.planned.toLocaleString()} planned`
              : "Under construction and planned, excluding delivered projects."
          }
        />
        {/* MPPI-00146. This read "3.9h ago" while the typical active listing
            had not been seen for 27 hours, because it measured the last
            ingestion RUN rather than the age of the data. */}
        <KPICard
          label="Data Age (median)"
          value={
            kpis
              ? kpis.data_freshness >= 0
                ? `${kpis.data_freshness}h`
                : "N/A"
              : "-"
          }
          subLabel={
            kpis && kpis.data_freshness_p90 >= 0
              ? `90th percentile ${kpis.data_freshness_p90}h — the stalest tenth`
              : undefined
          }
          icon={<CircleStackIcon className="h-6 w-6" />}
          color="cyan"
          href="/data-quality"
        />
      </div>

    </div>
  );
}
